import { TbRoute } from 'react-icons/tb'

import { exportToExcelButton } from '@/Containers/CrmSection/Components/Export'
import { StatusDictionarySelect } from '@/Ship/Components/FormFields/StatusDictionarySelect'
import { Hint } from '@/Ship/Components/Hint'
import { userManager } from '@/Core/Managers'

import { addApplicationKanbanMode } from '../Components/ApplicationKanban'
import {
    APPLICATION_DRAWER_WIDTH,
    APPLICATION_PAGE_SIZE,
    APPLICATION_REST_URL,
    TRIP_STATUS_CODES,
} from '../Contracts/IApplication'
import {
    APPLICATION_DETAIL,
    APPLICATION_FIELDS,
    APPLICATION_FORM,
    applicationColumnRenderers,
} from './ApplicationResource'

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

type Params = Record<string, unknown>

interface Condition {
    field: string
    op: string
    value: unknown
}

/**
 * Префільтр «Рейсів» (легасі `TripPage.jsx:7-18`), незмінний для **будь-якої**
 * ролі, зокрема адміністратора: я — менеджер клієнта **або** логіст, і статус —
 * один із шести від «Підтверджений» до «Успішно».
 *
 * Ship-трансформер будує лише `and` з плаского фільтра, тож гілку `or` і умову
 * статусу дописуємо поверх його результату (`list.paramsScope`). Той самий
 * трансформер будує запити лічильників рядка статусів, канбану й експорту —
 * вибірка в них одна.
 */
export const tripParamsScope = (base: Params): Params => {
    const raw = base['filters']
    const filters: { and?: Condition[]; or?: Condition[] } =
        typeof raw === 'string' && raw !== '' ? JSON.parse(raw) : {}
    const me = userManager.user?.['uuid']

    filters.and = [...(filters.and ?? []), { field: 'status.code', op: 'in', value: [...TRIP_STATUS_CODES] }]
    filters.or = [
        { field: 'manager.uuid', op: '=', value: me },
        { field: 'responsible.uuid', op: '=', value: me },
    ]

    base['filters'] = JSON.stringify(filters)

    return base
}

/** Підказка над таблицею «Рейсів» (`SPEC-021` §11). */
const TripScopeHint = () => (
    <Hint id="transport.trip.scope">
        Тут ваші заявки — де ви менеджер клієнта або логіст — від підтвердження до завершення. Нові й програні заявки
        сюди не потрапляють.
    </Hint>
)

/**
 * «Рейси» (`SPEC-021` §8.2; легасі `TripPage.jsx`) — той самий бек заявок
 * (`/services/applications`), інша вибірка й інший набір колонок:
 *
 *  - **префільтр** — {@see tripParamsScope}, рядок статусів лише з 6
 *    статусами префільтра, «Всі» за замовчуванням;
 *  - **14 колонок** у порядку легасі (`:29-34`), **13 фільтрів** — з «Державний
 *    номер ТЗ» (`:23-28`); без бейджа пропозицій;
 *  - **«Додати» немає** (`CREATED_FROM_PARENT`): заявки створюють на «Заявках»;
 *    редагування — `_permissions.update` (W-A), картка та сама;
 *  - **«Експорт в Excel»** — усім, хто бачить «Рейси» (Q2.6 1:1), з префільтром.
 */
export const TripResource: IResourceConfig = {
    title: 'Рейси',
    fields: {
        ...APPLICATION_FIELDS,
        status: { ...APPLICATION_FIELDS['status'], statusCodes: TRIP_STATUS_CODES },
    },
    table: {
        showColumns: [
            'id',
            'client',
            'status',
            'loadingCountries',
            'dateLoading',
            'unloadingCountries',
            'carrier',
            'numberVehicle',
            'typesCars',
            'dateUnloading',
            'manager',
            'responsible',
            'createdAt',
            'statusClient',
        ],
        dataSourceNoTransform: ['status', 'client', 'carrier'],
        eventHandlers: {
            onBuildColumns: { ApplicationColumnRenderers: applicationColumnRenderers(false) },
        },
    },
    filter: {
        includeFields: [
            'id',
            'client',
            'status',
            'loadingCountries',
            'dateLoading',
            'unloadingCountries',
            'carrier',
            'numberVehicle',
            'typesCars',
            'dateUnloading',
            'manager',
            'responsible',
            'createdAt',
        ],
        fieldProps: {
            status: { input: StatusDictionarySelect },
        },
    },
    list: {
        pagination: { pageSize: APPLICATION_PAGE_SIZE },
        paramsScope: tripParamsScope,
    },
    // Підказка — над панеллю фільтрів і таблицею (`CrudResourceTemplate`, `places.top`)
    places: { top: <TripScopeHint /> },
    headerPage: {
        eventHandlers: {
            onAfterInit: {
                AddApplicationKanbanMode: addApplicationKanbanMode({
                    statusCodes: TRIP_STATUS_CODES,
                    hideOffers: true,
                }),
            },
            onButtons: { ExportToExcel: exportToExcelButton('Рейси.xlsx') },
        },
    },
    form: APPLICATION_FORM,
    detailConfig: APPLICATION_DETAIL,
    drawer: {
        width: APPLICATION_DRAWER_WIDTH,
    },
    restUrl: APPLICATION_REST_URL,
    pageUrl: '/trips',
    pageConfig: {
        title: 'Рейси',
        icon: <TbRoute />,
    },
    settings: {
        searchFields: ['title'],
    },
}
